'use client';

import { useCallback } from 'react';
import type { FieldConfig, FormValues, FormErrors, FormTouched, FormInstance } from '../types';
import { saveFormState } from '../utils/storage';

interface UseFormChangeHandlerProps {
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  setCompletedForms?: React.Dispatch<
    React.SetStateAction<Record<string, import('../types').FormInstance[]>>
  >;
  clearCompletedFormGroups?: string[];
  localStorageKey?: string;
  currentStepId?: string;
  completedForms?: Record<string, FormInstance[]>;
}

const CLEAR_DEPS_WHEN_FIELD_CHANGES: Record<
  string,
  {
    whenValue: string | boolean;
    clearFieldIds: string[];
    completedFormGroupKeys?: string[];
    clearCompletedFormGroups?: string[];
  }
> = {
  hasDifferentNameAssets: {
    whenValue: 'No',
    clearFieldIds: [
      'differentNameAssetsFirstName',
      'differentNameAssetsMiddleName',
      'differentNameAssetsLastName',
    ],
    completedFormGroupKeys: ['estate-estimation-group'],
  },
  estateDisabilityBeneficiaryPreference: {
    whenValue: 'No',
    clearFieldIds: [
      'disabilityBeneficiaryFirstName',
      'disabilityBeneficiaryMiddleName',
      'disabilityBeneficiaryLastName',
    ],
    completedFormGroupKeys: ['other-information-estate-group'],
  },
  hasCharities: {
    whenValue: 'Yes',
    clearFieldIds: [],
  },
  willType: {
    whenValue: 'Single will',
    clearFieldIds: [],
    clearCompletedFormGroups: [
      'spouse-partner-personal-details-group',
      'spousePartnerChildrenFromPreviousRelationship-group',
      'spousePartnerGuardian-group',
    ],
  },
};

/**
 * Hook to handle form field changes
 */
export const useFormChangeHandler = ({
  setValues,
  setTouched,
  setErrors,
  setCompletedForms,
  localStorageKey,
  currentStepId,
  completedForms,
}: UseFormChangeHandlerProps) => {
  const handleChange = useCallback(
    (field: FieldConfig, rawValue: unknown) => {
      let value: unknown = rawValue;

      if (field.type === 'checkbox') {
        value = Boolean(rawValue);
      }

      const clearRule = CLEAR_DEPS_WHEN_FIELD_CHANGES[field.id];
      const shouldClearDeps =
        clearRule &&
        (typeof clearRule.whenValue === 'boolean'
          ? value === clearRule.whenValue
          : String(value) === String(clearRule.whenValue));

      const shouldPersistImmediately =
        field.id === 'hasChildren' || field.id === 'hasChildrenFromPreviousRelationship';

      setValues((prev) => {
        const next = { ...prev, [field.id]: value };
        if ((field.type === 'radio' || field.type === 'select') && field.options) {
          const selectedOption = field.options.find((opt) => opt.value === String(value));
          if (selectedOption) {
            next[`${field.id}ShortText`] = selectedOption.shortText || '';
          } else {
            delete next[`${field.id}ShortText`];
          }
        }
        if (shouldClearDeps) {
          clearRule.clearFieldIds.forEach((id) => delete next[id]);
        }
        // When spouse/partner same address is Yes, copy address from step-4-3 into spouse partner fields
        if (
          field.id === 'spousePartnerSameAddress' &&
          String(value) === 'Yes' &&
          prev.houseNumber !== undefined
        ) {
          next.spousePartnerHouseNumber = prev.houseNumber;
          next.spousePartnerStreet = prev.street;
          next.spousePartnerTown = prev.town;
          next.spousePartnerPostCode = prev.postCode;
        }

        if (shouldPersistImmediately && localStorageKey && currentStepId) {
          saveFormState(localStorageKey, {
            values: next,
            currentStepId,
            completedForms: completedForms ?? {},
          });
        }

        return next;
      });

      setTouched((prev) => ({
        ...prev,
        [field.id]: true,
      }));

      setErrors((prev) => {
        const next = { ...prev, [field.id]: undefined };
        if (shouldClearDeps && clearRule) {
          clearRule.clearFieldIds.forEach((id) => {
            delete next[id];
          });
        }
        return next;
      });

      if (shouldClearDeps && clearRule?.completedFormGroupKeys?.length && setCompletedForms) {
        const { clearFieldIds, completedFormGroupKeys } = clearRule;
        setCompletedForms((prev) => {
          let hasChanges = false;
          const updated = { ...prev };
          for (const groupKey of completedFormGroupKeys) {
            const instances = prev[groupKey];
            if (!instances?.length) continue;
            const nextInstances = instances.map((inst) => {
              let dataChanged = false;
              const nextData = { ...inst.data };
              clearFieldIds.forEach((id) => {
                if (id in nextData) {
                  delete nextData[id];
                  dataChanged = true;
                }
              });
              if (!dataChanged) return inst;
              hasChanges = true;
              return { ...inst, data: nextData };
            });
            if (hasChanges) updated[groupKey] = nextInstances;
          }
          return hasChanges ? updated : prev;
        });
      }
      if (shouldClearDeps && clearRule?.clearCompletedFormGroups?.length && setCompletedForms) {
        setCompletedForms((prev) => {
          let hasChanges = false;
          const updated = { ...prev };
          for (const groupKey of clearRule.clearCompletedFormGroups!) {
            if (prev[groupKey]?.length) {
              updated[groupKey] = [];
              hasChanges = true;
            }
          }
          return hasChanges ? updated : prev;
        });
      }
    },
    [
      setValues,
      setTouched,
      setErrors,
      setCompletedForms,
      localStorageKey,
      currentStepId,
      completedForms,
    ]
  );

  return { handleChange };
};
